import React, { useState, useRef, useEffect } from 'react';
import { url } from '../../util/config';

const chatbotWindowStyle = {
    position: 'fixed',
    bottom: '32px',
    right: '32px',
    width: '390px',
    minHeight: '540px',
    maxHeight: '640px',
    background: 'rgba(255,255,255,0.85)',
    borderRadius: '24px',
    boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: '1px solid #e3e3e3',
    backdropFilter: 'blur(8px)',
};

const headerStyle = {
    background: 'linear-gradient(90deg, #007bff 0%, #00c6ff 100%)',
    color: '#fff',
    padding: '20px 16px 16px 16px',
    fontWeight: 700,
    fontSize: '20px',
    borderTopLeftRadius: '24px',
    borderTopRightRadius: '24px',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
    position: 'relative',
};

const chatBodyStyle = {
    flex: 1,
    padding: '18px 16px',
    background: 'rgba(245,248,255,0.95)',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minHeight: '350px',
};

const inputAreaStyle = {
    padding: '16px',
    background: 'rgba(255,255,255,0.95)',
    borderTop: '1px solid #e3e3e3',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
};

const userMsgStyle = {
    alignSelf: 'flex-end',
    background: 'linear-gradient(90deg, #007bff 0%, #00c6ff 100%)',
    color: '#fff',
    borderRadius: '18px 18px 0 18px',
    padding: '10px 18px',
    maxWidth: '75%',
    fontSize: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.09)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    animation: 'fadeIn 0.4s',
};

const botMsgStyle = {
    alignSelf: 'flex-start',
    background: 'rgba(255,255,255,0.85)',
    color: '#333',
    borderRadius: '18px 18px 18px 0',
    padding: '10px 18px',
    maxWidth: '75%',
    fontSize: '16px',
    border: '1px solid #e3e3e3',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    animation: 'fadeIn 0.4s',
};

const avatarBotStyle = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)',
    boxShadow: '0 0 8px #00c6ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '8px',
};

const avatarUserStyle = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #007bff 0%, #00c6ff 100%)',
    boxShadow: '0 0 8px #007bff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '8px',
};

const loadingDotsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    height: '32px',
};

const dotStyle = {
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    background: '#007bff',
    opacity: 0.7,
    animation: 'dotPulse 1.2s infinite',
};

const closeBtnStyle = {
    position: 'absolute',
    top: '14px',
    right: '18px',
    background: 'transparent',
    border: 'none',
    color: '#fff',
    fontSize: '22px',
    cursor: 'pointer',
};

const DataConverseChatbot = ({ open, onClose, dataConverseId, messages, onMessagesChange }) => {
    /* const [messages, setMessages] = useState([
        { from: 'bot', text: 'Welcome to Data Converse AI Chatbot, How can I assist you?' }
    ]); */
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const chatBodyRef = useRef();

    // Fetch chat history when chatbot opens for a new DataConverseId
    useEffect(() => {
        if (open && dataConverseId && !historyLoaded) {
            fetchChatHistory();
        }
        // eslint-disable-next-line
    }, [open, dataConverseId]);

    const fetchChatHistory = async () => {
        try {
            const token = localStorage.getItem('token');
            const docId = dataConverseId;
            const apiUrl = `${url}/chathistory/${encodeURIComponent(docId)}`;

            // Fetch with empty question to get only history
            const response = await fetch(apiUrl, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.ok) throw new Error('API Error');
            const result = await response.json();

            let loadedMessages = [
                { from: 'bot', text: 'Welcome to Data Converse AI Chatbot, How I can help you?' }
            ];

            if (Array.isArray(result)) {
                result.forEach(h => {
                    loadedMessages.push({ from: 'user', text: h.question, isHistory: true });
                    loadedMessages.push({ from: 'bot', text: h.answer, isHistory: true });
                });
            }

            onMessagesChange(loadedMessages);
            setHistoryLoaded(true);
        } catch (err) {
            onMessagesChange([
                { from: 'bot', text: 'Welcome to Data Converse AI Chatbot, How I can help you?' },
                { from: 'bot', text: 'Sorry, unable to load previous chat history.' }
            ]);
            setHistoryLoaded(true);
        }
    };

    useEffect(() => {
        // Scroll to bottom when messages change
        if (chatBodyRef.current) {
            chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
    }, [messages, loading]);

    const handleSend = async () => {
        if (!input.trim()) return;
        const newMessages = [...messages, { from: 'user', text: input }];
        onMessagesChange(newMessages);
        //setMessages(prev => [...prev, { from: 'user', text: input }]);
        setInput('');
        setLoading(true);

        setTimeout(() => {
            if (chatBodyRef.current) {
                chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
            }
        }, 100);

        try {
            // Prepare API call
            const userId = localStorage.getItem('userid'); // Or get from props/context
            const docId = dataConverseId;
            const apiUrl = `http://localhost:8000/chatqna?docid=${encodeURIComponent(docId)}&userid=${encodeURIComponent(userId)}`;

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ question: input }),
            });

            if (!response.ok) throw new Error('API Error');
            const result = await response.json();

            // If history is not yet in messages, add all history as chat
            let updatedMessages = [...newMessages];
            //const hasHistory = messages.some(m => m.from === 'bot' && m.isHistory);

            // if (Array.isArray(result.history) && !hasHistory) {
            //     // Add all history as bot/user pairs
            //     result.history.forEach(h => {
            //         updatedMessages.push({ from: 'user', text: h.question, isHistory: true });
            //         updatedMessages.push({ from: 'bot', text: h.answer, isHistory: true });
            //     });
            // } else if (result.answer) {
            //     // Only add the latest Q/A
            //     updatedMessages.push({ from: 'bot', text: result.answer });
            // }

            if (result.answer) {
                updatedMessages.push({ from: 'bot', text: result.answer });
            }

            onMessagesChange(updatedMessages);
        } catch (err) {
            onMessagesChange([
                ...messages,
                { from: 'user', text: input },
                { from: 'bot', text: 'Sorry, there was a problem reaching the AI. Please try again.' }
            ]);
        }
        setLoading(false);

        setTimeout(() => {
            if (chatBodyRef.current) {
                chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
            }
        }, 100);
    };

    // Reset historyLoaded when closing or switching DataConverseId
    useEffect(() => {
        if (!open) setHistoryLoaded(false);
    }, [open, dataConverseId]);

    if (!open) return null;

    return (
        <div style={chatbotWindowStyle}>
            <div style={headerStyle}>
                Data Converse AI
                <button style={closeBtnStyle} onClick={onClose} title="Close">&times;</button>
            </div>
            <div style={chatBodyStyle} ref={chatBodyRef}>
                {messages.map((msg, idx) => (
                    <div key={idx} style={msg.from === 'user' ? userMsgStyle : botMsgStyle}>
                        {msg.from === 'bot' && (
                            <span style={avatarBotStyle}>
                                <img src="/chatbot.png" alt="Bot" style={{ width: 24, height: 24 }} />
                            </span>
                        )}
                        <span>{msg.text}</span>
                        {msg.from === 'user' && (
                            <span style={avatarUserStyle}>
                                <svg width="22" height="22" viewBox="0 0 22 22">
                                    <circle cx="11" cy="11" r="11" fill="#fff" />
                                    <text x="11" y="16" textAnchor="middle" fontSize="13" fill="#007bff">U</text>
                                </svg>
                            </span>
                        )}
                    </div>
                ))}
                {loading && (
                    <div style={botMsgStyle}>
                        <span style={avatarBotStyle}>
                            <img src="/chatbot.png" alt="Bot" style={{ width: 24, height: 24 }} />
                        </span>
                        <div style={loadingDotsStyle}>
                            <span style={{ ...dotStyle, animationDelay: '0s' }}></span>
                            <span style={{ ...dotStyle, animationDelay: '0.2s' }}></span>
                            <span style={{ ...dotStyle, animationDelay: '0.4s' }}></span>
                        </div>
                    </div>
                )}
            </div>
            <form
                style={inputAreaStyle}
                onSubmit={e => {
                    e.preventDefault();
                    handleSend();
                }}
            >
                <input
                    type="text"
                    className="form-control"
                    style={{ flex: 1, borderRadius: '12px', fontSize: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
                    placeholder="Type your question..."
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    disabled={loading}
                    autoFocus
                />
                <button
                    type="submit"
                    className="btn btn-primary"
                    style={{
                        borderRadius: '12px',
                        fontWeight: 600,
                        fontSize: '16px',
                        padding: '8px 20px',
                        boxShadow: '0 2px 8px rgba(0,123,255,0.12)',
                        transition: 'background 0.2s',
                    }}
                    disabled={loading || !input.trim()}
                >
                    Send
                </button>
            </form>
            <style>{`
                @keyframes dotPulse {
                    0% { opacity: 0.3; transform: scale(1); }
                    50% { opacity: 1; transform: scale(1.3); }
                    100% { opacity: 0.3; transform: scale(1); }
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px);}
                    to { opacity: 1; transform: translateY(0);}
                }
            `}</style>
        </div>
    );
};

export default DataConverseChatbot;